import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiUserPlus, FiEdit, FiCheckCircle } from "react-icons/fi";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const HowToJoin = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const ctaButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 75%",
        toggleActions: "play none none none",
      },
    });

    tl.from(section.querySelectorAll(".step-card"), {
      opacity: 0,
      x: -50,
      stagger: 0.2,
      duration: 0.8,
      ease: "power2.out",
    }).from(
      section.querySelector(".cta-container"),
      {
        opacity: 0,
        scale: 0.9,
        duration: 1,
        ease: "power3.out",
      },
      "-=0.5"
    );

    // CTA Button pulse and glow
    const ctaButton = ctaButtonRef.current;
    if (ctaButton) {
      gsap.fromTo(
        ctaButton,
        { scale: 1, boxShadow: "0 0 0 0 rgba(251, 191, 36, 0.7)" },
        {
          scale: 1.05,
          repeat: 1,
          yoyo: true,
          duration: 0.8,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: ctaButton,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        }
      );

      const hoverTl = gsap
        .timeline({ paused: true })
        .to(ctaButton, { scale: 1.05, duration: 0.3, ease: "power2.out" })
        .to(
          ctaButton,
          {
            boxShadow: "0 0 20px 5px rgba(251, 191, 36, 0.4)",
            duration: 0.3,
            ease: "power2.out",
          },
          0
        );

      ctaButton.addEventListener("mouseenter", () => hoverTl.play());
      ctaButton.addEventListener("mouseleave", () => hoverTl.reverse());
    }

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const steps = [
    {
      icon: <FiUserPlus className="h-7 w-7 text-nature-green-dark" />,
      title: "Create Your Account",
      text: "Become a verified member of our digital community.",
    },
    {
      icon: <FiEdit className="h-7 w-7 text-nature-green-dark" />,
      title: "Enter Your Date of Birth",
      text: "Your birth date is used to place you in the correct age grade.",
    },
    {
      icon: <FiCheckCircle className="h-7 w-7 text-nature-green-dark" />,
      title: "Automatic Assignment",
      text: "Our system assigns you to your grade, connecting you with peers.",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2 md:gap-16">
        {/* Steps */}
        <div>
          <h2 className="font-serif text-3xl font-semibold leading-snug text-nature-green-dark md:text-4xl">
            Become a Part of the Legacy
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-gray-700">
            Joining an age grade is a seamless process. Follow these simple
            steps to find your place within our community structure.
          </p>
          <div className="mt-10 space-y-6">
            {steps.map((step, index) => (
              <div key={index} className="step-card flex items-start gap-5">
                <div className="flex-shrink-0 rounded-full bg-stone-100 p-4">
                  {step.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="cta-container flex flex-col items-center justify-center rounded-2xl border border-stone-200/80 bg-stone-50 p-8 text-center shadow-lg md:p-12">
          <h3 className="text-2xl font-bold text-gray-800">Ready to Join?</h3>
          <p className="mt-3 max-w-sm text-gray-600">
            Your journey starts here. Create an account to connect with your
            peers and contribute to our shared future.
          </p>
          <Link to="/register" className="mt-8">
            <button
              ref={ctaButtonRef}
              className="transform-gpu rounded-full bg-amber-400 px-10 py-4 text-lg font-bold text-nature-green-dark transition-transform"
            >
              Register Now
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HowToJoin;
